/**
 * RankChip — small rounded chip showing "#5" (league position).
 *
 * Extracted from `IhsOverview.rankChip` (Phase 0b). Positioned over a
 * crest in current call sites, but the primitive ships plain — the
 * positioning is the consumer's job (absolute parent + offsets).
 *
 * Shape: { rank: number; tone?: 'neutral'|'positive'|'negative' }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './RankChip.module.css';

const dataShape = z.object({
  rank: z.number().int().positive(),
  tone: z.enum(['neutral', 'positive', 'negative']).optional(),
});

export const META: PrimitiveMeta = {
  name: 'rank-chip',
  description: 'Small rounded chip showing "#N" (league position).',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

interface RankChipProps {
  rank: number;
  /** Colour tone. Defaults to 'neutral'. */
  tone?: 'neutral' | 'positive' | 'negative';
}

function toneClass(tone: NonNullable<RankChipProps['tone']>): string {
  if (tone === 'positive') return styles.positive ?? '';
  if (tone === 'negative') return styles.negative ?? '';
  return styles.neutral ?? '';
}

export function RankChip({ rank, tone = 'neutral' }: RankChipProps): ReactElement {
  return (
    <span className={`${styles.chip} ${toneClass(tone)}`}>#{rank}</span>
  );
}

export default RankChip;
